import {
  Message,
  CreateCanvasDocumentContract,
  UpsertCanvasMessageContract,
  SaveToResourcesContract,
  SaveToDocumentsContract,
  GenerateTitleContract,
  AIResponseCanvasMessage,
} from "winslow-core";
import { getRequest, postRequest, deleteRequest } from "./client";
import { LinkRecord } from "winslow-core";

export class CanvasModeService {
  private static readonly BASE_URL = "/canvas-mode";

  /**
   * Create a new canvas message from existing message
   */
  static async createCanvasMessage(
    request: CreateCanvasDocumentContract["Body"]
  ): Promise<AIResponseCanvasMessage | null> {
    const fetchURL = `${this.BASE_URL}/create`;
    return await postRequest(
      fetchURL,
      request,
      "CanvasModeService/createCanvasMessage"
    );
  }

  /**
   * Get a canvas message by ID
   */
  static async getCanvasMessage(
    id: string
  ): Promise<AIResponseCanvasMessage | null> {
    const fetchURL = `${this.BASE_URL}/${id}`;
    return await getRequest(fetchURL, "CanvasModeService/getCanvasMessage");
  }

  /**
   * Get all canvas messages for a user
   */
  static async getUserCanvasMessages(
    userId: string
  ): Promise<AIResponseCanvasMessage[]> {
    const fetchURL = `${this.BASE_URL}/user/${userId}`;
    const result = await getRequest<AIResponseCanvasMessage[]>(
      fetchURL,
      "CanvasModeService/getUserCanvasMessages"
    );
    return result || [];
  }

  /**
   * Update a canvas message
   */
  static async updateCanvasMessage(
    request: UpsertCanvasMessageContract["Body"]
  ): Promise<AIResponseCanvasMessage | null> {
    const fetchURL = `${this.BASE_URL}/update`;
    return await postRequest(
      fetchURL,
      request,
      "CanvasModeService/updateCanvasMessage"
    );
  }

  /**
   * Delete a canvas message
   */
  static async deleteCanvasMessage(id: string): Promise<boolean> {
    const fetchURL = `${this.BASE_URL}/${id}`;
    const result = await deleteRequest(
      fetchURL,
      "CanvasModeService/deleteCanvasMessage"
    );
    return result !== null;
  }

  /**
   * Generate an AI title for document content
   */
  static async generateTitle(
    request: GenerateTitleContract["Body"]
  ): Promise<string> {
    const fetchURL = `${this.BASE_URL}/generate-title`;
    const result = await postRequest<
      GenerateTitleContract["Body"],
      { title: string }
    >(fetchURL, request, "CanvasModeService/generateTitle");
    return result?.title || "Untitled Document";
  }

  /**
   * Edit a canvas message using AI
   */
  static async editCanvasMessageWithAI(request: {
    id: number;
    prompt: string;
    content: string;
  }): Promise<AIResponseCanvasMessage | null> {
    const fetchURL = `${this.BASE_URL}/ai-edit`;
    return await postRequest(
      fetchURL,
      request,
      "CanvasModeService/editCanvasMessageWithAI"
    );
  }

  /**
   * Save document to resources (knowledge base)
   */
  static async saveToResources(
    request: SaveToResourcesContract["Body"]
  ): Promise<{ success: boolean; linkRecordId?: string } | null> {
    const fetchURL = `${this.BASE_URL}/save-to-resources`;
    const result = await postRequest<
      SaveToResourcesContract["Body"],
      { success: boolean; linkRecordId?: string }
    >(
      fetchURL,
      request,
      "CanvasModeService/saveToResources"
    );
    return result;
  }

  static async getCanvasResource(
    messageId: number
  ): Promise<LinkRecord | null> {
    const fetchURL = `${this.BASE_URL}/resource/${messageId}`;
    return await getRequest(fetchURL, "CanvasModeService/getCanvasResource");
  }

  /**
   * Save document to company documents
   */
  static async saveToDocuments(
    request: SaveToDocumentsContract["Body"]
  ): Promise<boolean> {
    const fetchURL = `${this.BASE_URL}/save-to-documents`;
    const result = await postRequest(
      fetchURL,
      request,
      "CanvasModeService/saveToDocuments"
    );
    return result !== null;
  }

  /**
   * Helper method to convert markdown to HTML
   */
  private static markdownToHtml(markdown: string): string {
    return markdown
      .replace(/^# (.*$)/gm, "<h1>$1</h1>")
      .replace(/^## (.*$)/gm, "<h2>$1</h2>")
      .replace(/^### (.*$)/gm, "<h3>$1</h3>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/`(.*?)`/g, "<code>$1</code>")
      .replace(/\n\n/g, "</p><p>")
      .replace(/\n/g, "<br>");
  }
}

export default CanvasModeService;
